/**
 * Web client blocks API. The server is the source of truth; every call is RPC.
 */
import { cacheAppSession, readCachedAppSession } from '../auth/session-cache';
import { isMissingBlockError } from '../net/api-error';

import type { BlockRecord, MemberRecord } from './types';
import * as remote from './api.web';

export type { MemberManagement, PendingMember, PageDocument } from './api.web';

export async function fetchBlockTree(parentId: string | null): Promise<BlockRecord[]> {
  return remote.fetchBlockTree(parentId);
}

export async function fetchNavBlocks(): Promise<BlockRecord[]> {
  return remote.fetchNavBlocks();
}

export async function fetchBlock(id: string): Promise<BlockRecord> {
  return remote.fetchBlock(id);
}

function cacheMemberSession(member: MemberRecord): void {
  const cached = readCachedAppSession();
  cacheAppSession({
    id: member.id,
    name: member.displayName,
    email: cached?.email ?? `${member.id}@local`,
    image: member.avatarUrl,
  });
}

export async function fetchCurrentMember(): Promise<MemberRecord> {
  const member = await remote.fetchCurrentMember();
  cacheMemberSession(member);
  return member;
}

export async function createBlock(
  input: Parameters<typeof remote.createBlock>[0],
): ReturnType<typeof remote.createBlock> {
  return remote.createBlock(input);
}

export async function updateBlock(
  id: string,
  input: Parameters<typeof remote.updateBlock>[1],
): ReturnType<typeof remote.updateBlock> {
  return remote.updateBlock(id, input);
}

export async function deleteBlock(id: string): Promise<void> {
  try {
    await remote.deleteBlock(id);
  } catch (err) {
    if (!isMissingBlockError(err)) {
      throw err;
    }
  }
}

export async function restoreBlock(id: string): Promise<BlockRecord> {
  return remote.restoreBlock(id);
}

export async function reorderBlocks(
  updates: Parameters<typeof remote.reorderBlocks>[0],
): ReturnType<typeof remote.reorderBlocks> {
  return remote.reorderBlocks(updates);
}

export async function reparentBlock(
  id: string,
  input: Parameters<typeof remote.reparentBlock>[1],
): ReturnType<typeof remote.reparentBlock> {
  return remote.reparentBlock(id, input);
}

export async function fetchPageDocument(
  pageId: string,
): ReturnType<typeof remote.fetchPageDocument> {
  return remote.fetchPageDocument(pageId);
}

export const prefetchPageDocument = remote.prefetchPageDocument;

export async function fetchPageContentBlocks(pageId: string): Promise<BlockRecord[]> {
  const document = await fetchPageDocument(pageId);
  return document.contentBlocks;
}

export async function fetchFamilyRootId(): Promise<string | null> {
  const roots = await fetchBlockTree(null);
  const family = roots.find((b) => b.type === 'page' && b.title === 'Family');
  return family?.id ?? null;
}

export async function fetchLibraryRows(
  filter: Parameters<typeof remote.fetchLibraryRows>[0],
): ReturnType<typeof remote.fetchLibraryRows> {
  return remote.fetchLibraryRows(filter);
}

export async function fetchMembers(): Promise<MemberRecord[]> {
  return remote.fetchMembers();
}

export async function fetchFavorites(): ReturnType<typeof remote.fetchFavorites> {
  return remote.fetchFavorites();
}

export async function starBlock(blockId: string): Promise<void> {
  return remote.starBlock(blockId);
}

export async function unstarBlock(blockId: string): Promise<void> {
  return remote.unstarBlock(blockId);
}

export const fetchLlmPrivatePages = remote.fetchLlmPrivatePages;
export const markLlmPrivateBlock = remote.markLlmPrivateBlock;
export const clearLlmPrivateBlock = remote.clearLlmPrivateBlock;

export const presignUpload = remote.presignUpload;
export const presignDownload = remote.presignDownload;
export async function updateMemberProfile(
  input: { displayName: string },
): ReturnType<typeof remote.updateMemberProfile> {
  return remote.updateMemberProfile(input);
}

export const fetchMemberManagement = remote.fetchMemberManagement;
export const addMemberEmail = remote.addMemberEmail;
export const removePendingMember = remote.removePendingMember;
export const removeMember = remote.removeMember;
